import { useState } from "react";

import { DUE_DATE_ORDER, DUE_DATE_PAGES, type DueDatePage } from "../../lib/due-date-pages";
import { formatDay, plural, todayInFrance } from "../../lib/format";
import { ageOn, daysBetween, dating, type DatingInput } from "../../lib/pregnancy";
import { BabyThisWeek, PregnancyTimeline } from "./pregnancy-visuals";
import { ToolLayout } from "./tool-layout";

export const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const DISCLAIMER =
  "Résultats indicatifs : seuls votre sage-femme ou votre médecin fixent le terme de votre grossesse.";

export function DueDatePageView({ page }: { page: DueDatePage }) {
  return (
    <ToolLayout
      copy={page}
      eyebrow="Calculateur gratuit"
      tabs={DUE_DATE_ORDER.map((method) => ({
        label: DUE_DATE_PAGES[method].tab,
        to: DUE_DATE_PAGES[method].path,
        current: method === page.method,
      }))}
      disclaimer={DISCLAIMER}
    >
      {/* Keyed: switching method starts from an empty form. */}
      <Calculator key={page.method} method={page.method} />
    </ToolLayout>
  );
}

type Fields = Record<string, string>;

const INITIAL: Record<DatingInput["method"], Fields> = {
  lmp: { lmp: "", cycleDays: "28" },
  conception: { conception: "" },
  ultrasound: { scanDate: "", weeks: "", days: "0" },
  ivf: { transferDate: "", embryoDay: "5" },
};

/** The form's fields as a dating input, or null while incomplete or out of range. */
const toInput = (method: DatingInput["method"], fields: Fields): DatingInput | null => {
  const day = (key: string) => {
    const value = fields[key] ?? "";
    return ISO_DAY.test(value) ? value : null;
  };
  const whole = (key: string, min: number, max: number) => {
    const value = Number(fields[key]);
    return fields[key] !== "" && Number.isInteger(value) && value >= min && value <= max
      ? value
      : null;
  };
  switch (method) {
    case "lmp": {
      const lmp = day("lmp");
      const cycleDays = whole("cycleDays", 21, 45);
      return lmp && cycleDays ? { method, lmp, cycleDays } : null;
    }
    case "conception": {
      const conception = day("conception");
      return conception ? { method, conception } : null;
    }
    case "ultrasound": {
      const scanDate = day("scanDate");
      const weeks = whole("weeks", 5, 42);
      const days = whole("days", 0, 6);
      return scanDate && weeks !== null && days !== null ? { method, scanDate, weeks, days } : null;
    }
    case "ivf": {
      const transferDate = day("transferDate");
      const embryoDay = fields.embryoDay === "3" ? 3 : 5;
      return transferDate ? { method, transferDate, embryoDay } : null;
    }
  }
};

function Calculator({ method }: { method: DatingInput["method"] }) {
  const [fields, setFields] = useState<Fields>(INITIAL[method]);
  const input = toInput(method, fields);
  const field = (key: string) => ({
    value: fields[key] ?? "",
    onChange: (event: { target: { value: string } }) =>
      setFields((current) => ({ ...current, [key]: event.target.value })),
  });

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        {method === "lmp" && (
          <>
            <label className="label">
              Premier jour des dernières règles
              <input type="date" className="input" {...field("lmp")} />
            </label>
            <label className="label">
              Durée du cycle (jours)
              <input type="number" min={21} max={45} className="input" {...field("cycleDays")} />
            </label>
          </>
        )}
        {method === "conception" && (
          <label className="label">
            Date de conception
            <input type="date" className="input" {...field("conception")} />
          </label>
        )}
        {method === "ultrasound" && (
          <>
            <label className="label sm:col-span-2">
              Date de l'échographie
              <input type="date" className="input" {...field("scanDate")} />
            </label>
            <label className="label">
              Semaines d'aménorrhée (SA)
              <input type="number" min={5} max={42} className="input" {...field("weeks")} />
            </label>
            <label className="label">
              Jours en plus
              <input type="number" min={0} max={6} className="input" {...field("days")} />
            </label>
          </>
        )}
        {method === "ivf" && (
          <>
            <label className="label">
              Date du transfert
              <input type="date" className="input" {...field("transferDate")} />
            </label>
            <label className="label">
              Âge de l'embryon
              <select className="input" {...field("embryoDay")}>
                <option value="3">J3 (3 jours)</option>
                <option value="5">J5 (blastocyste)</option>
              </select>
            </label>
          </>
        )}
      </div>
      {input ? (
        <Result input={input} />
      ) : (
        <p className="text-center text-muted">Le résultat s'affiche dès la date saisie.</p>
      )}
    </>
  );
}

/** Rendered only once the visitor has typed a date: never on the server, so "today" is safe. */
function Result({ input }: { input: DatingInput }) {
  const result = dating(input);
  const today = todayInFrance();
  const age = ageOn(result.amenorrhoeaStart, today);
  const elapsed = age ? age.weeks * 7 + age.days : null;
  const remaining = daysBetween(today, result.dueDate);
  return (
    <div className="grid gap-6 border-t-[1.5px] border-line pt-6" aria-live="polite">
      <div className="grid justify-items-center gap-2 text-center">
        <p className="font-bold text-muted">Date prévue d'accouchement</p>
        <p className="font-soft text-4xl font-medium text-accent-text sm:text-5xl">
          {formatDay(result.dueDate)}
        </p>
        <div className="flex flex-wrap justify-center gap-2 text-sm font-bold">
          {age && (
            <span className="rounded-full bg-ink px-3 py-1 text-surface">
              {age.weeks} SA{age.days > 0 && ` + ${age.days} j`} aujourd'hui
            </span>
          )}
          {remaining > 0 && (
            <span className="rounded-full bg-butter px-3 py-1">
              J-{remaining}, encore {plural(Math.floor(remaining / 7), "semaine", "semaines")}
            </span>
          )}
        </div>
        <p className="text-sm text-muted">
          Terme à 41 SA, calcul français. À 40 SA (calcul international) :{" "}
          {formatDay(result.dueDate40)}.
        </p>
      </div>

      <PregnancyTimeline result={result} todaySa={elapsed} />

      {age && <BabyThisWeek weeks={age.weeks} title="Cette semaine" />}

      <div className="grid gap-3 sm:grid-cols-3">
        <DateTile label="Début de grossesse" value={formatDay(result.start)} />
        {result.scans.map((scan, index) => (
          <DateTile
            key={scan.label}
            label={`Échographie ${index + 1}`}
            value={`${formatDay(scan.from)} au ${formatDay(scan.to)}`}
          />
        ))}
        <DateTile label="Terme international (40 SA)" value={formatDay(result.dueDate40)} />
        <DateTile label="Terme français (41 SA)" value={formatDay(result.dueDate)} />
      </div>
    </div>
  );
}

export function DateTile({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="grid gap-1 rounded-2xl bg-page p-4">
      <span className="text-sm text-muted">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}
